import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer, ShieldAlert, Link2, Gauge, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { authAPI, displayTimeAPI } from '../services/api';
import type { DtvXVerification, DtvXWindow } from '../types';
import KpiCard from '../components/KpiCard';
import DisplayTimeLive from '../components/DisplayTimeLive';

// Ad display time (sasadmin only). Two tabs:
//  - "Live" (default, 2026-10-02): production display time on all four platforms
//    (ad_display_time, captured since 2026-10-01) — DisplayTimeLive.
//  - "X verification (10/1)": the stopwatch-calibrated live-capture rounds on the
//    internal test line, kept as the evidence behind the X result.

const jst = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false }) : '—';
const sec = (ms: number) => (ms / 1000).toFixed(2);

export default function DisplayTimeVerification() {
  const { t } = useTranslation();
  const [role, setRole] = useState<string | null>(null);
  const isSasadmin = role === 'sasadmin';
  const [data, setData] = useState<DtvXVerification | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'live' | 'x'>('live');

  useEffect(() => {
    authAPI.checkAdminAuth().then(res => {
      if (res.authenticated && res.user) setRole(res.user.role ?? 'sasadmin');
    }).catch(() => setRole('sys-operator'));
  }, []);

  useEffect(() => {
    if (!isSasadmin || tab !== 'x' || data) return;
    displayTimeAPI.getXVerification().then(setData).catch(e => setError(String(e)));
  }, [isSasadmin, tab, data]);

  const windows = data?.windows ?? [];
  const calib = useMemo(() => windows.filter(w => w.stopwatchS !== null), [windows]);
  const maxMs = useMemo(() => Math.max(1, ...windows.map(w => w.onScreenMs)), [windows]);

  if (role && !isSasadmin) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center p-6">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>{t('displayTime.adminOnly', 'この画面は管理者(sasadmin)専用です。')}</p>
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const u = t('displayTime.sec', '秒');
  const tabLabel = (w: DtvXWindow) =>
    w.tab === 'for_you' ? t('displayTime.tabForYou', 'おすすめ') : t('displayTime.tabFollowing', 'フォロー中');

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <Timer className="w-6 h-6 text-teal-500" />
          {t('displayTime.pageTitle', '広告表示時間')}
        </h1>
        <div className="flex gap-2 mb-5 border-b dark:border-gray-700">
          {(['live', 'x'] as const).map(k => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k
                ? 'border-teal-500 text-teal-700 dark:text-teal-300'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>
              {k === 'live' ? t('displayTime.tabLive', '本番データ（全媒体）') : t('displayTime.tabX', 'X 実機検証（10/1）')}
            </button>
          ))}
        </div>
        {tab === 'live' && isSasadmin && <DisplayTimeLive />}
        {tab === 'x' && (<>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('displayTime.subtitle', '2026年10月1日 ／ 社内検証用回線（SAS-E5C3-B333）／ 本番サーバ経由で取得')}
        </p>

        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-4 mb-6 text-sm text-amber-900 dark:text-amber-200 flex gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <b>{t('displayTime.bannerTitle', 'これは検証データです。')}</b>{' '}
            {t('displayTime.bannerBodyV2',
              '2026年10月1日に社内検証用回線で行った実機検証（ストップウォッチ校正）の記録です。本番では同日から全媒体の表示時間を常時取得しています（「本番データ」タブ）。')}
          </div>
        </div>

        {error && <p className="text-red-500 mb-4">{error}</p>}
        {!data && !error && <p className="text-gray-500">{t('common.loading', '読み込み中…')}</p>}
        {data && !data.hasData && <p className="text-gray-500">{t('displayTime.noData', 'データがありません。')}</p>}

        {s && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KpiCard icon={Timer} color="text-teal-500"
                label={t('displayTime.kpiWindows', '表示時間を計測できた広告')}
                value={`${s.windows}`}
                sub={t('displayTime.kpiWindowsSub', '全て異なる値 {{n}}/{{m}}', { n: s.distinctValues, m: s.windows })} />
              <KpiCard icon={Link2} color="text-indigo-500"
                label={t('displayTime.kpiJoined', '当社の広告データと紐付け')}
                value={`${s.joined} / ${s.windows}`}
                sub={t('displayTime.kpiJoinedSub', 'おすすめ {{a}}/{{b}} ・ フォロー中 {{c}}/{{d}}',
                  { a: s.forYou.joined, b: s.forYou.windows, c: s.following.joined, d: s.following.windows })} />
              <KpiCard icon={Clock} color="text-sky-500"
                label={t('displayTime.kpiMedian', '画面表示時間（中央値）')}
                value={`${sec(s.medianMs)} ${u}`}
                sub={t('displayTime.kpiRange', '最短 {{a}} 秒 ・ 最長 {{b}} 秒', { a: sec(s.minMs), b: sec(s.maxMs) })} />
              <KpiCard icon={Gauge} color="text-emerald-500"
                label={t('displayTime.kpiCalib', 'ストップウォッチ校正')}
                value={`${calib.filter(w => w.onScreenMs >= (w.stopwatchS ?? 0) * 1000).length} / ${calib.length}`}
                sub={t('displayTime.kpiCalibSub', '記録 ≧ 静止時間')} />
            </div>

            <section className="bg-white dark:bg-[#231d17] rounded-xl shadow-sm p-5 mb-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                {t('displayTime.calibTitle', 'ストップウォッチ校正（2回目）')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {t('displayTime.calibNote',
                  'X の記録は「画面に出ていた時間」全体で、画面に入る途中・計測開始前・画面から出る途中も含むため、静止時間より常に少し長くなります。')}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="py-2 pr-3">{t('displayTime.colTab', 'タブ')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colAdvertiser', '広告主')}</th>
                    <th className="py-2 pr-3 text-right">{t('displayTime.colStopwatch', '静止（ストップウォッチ）')}</th>
                    <th className="py-2 pr-3 text-right">{t('displayTime.colMeasured', 'X の記録')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colCheck', '記録 ≧ 静止')}</th>
                  </tr></thead>
                  <tbody>
                    {calib.map(w => (
                      <tr key={w.impressionId} className="border-b dark:border-gray-800 text-gray-800 dark:text-gray-200">
                        <td className="py-2 pr-3">{tabLabel(w)}</td>
                        <td className="py-2 pr-3">{w.advertiserName ?? <span className="text-amber-600">{t('displayTime.unjoined', '未紐付け')}</span>}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{w.stopwatchS?.toFixed(0)} {u}</td>
                        <td className="py-2 pr-3 text-right tabular-nums font-semibold">{sec(w.onScreenMs)} {u}</td>
                        <td className="py-2 pr-3">{w.onScreenMs >= (w.stopwatchS ?? 0) * 1000
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : '✗'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white dark:bg-[#231d17] rounded-xl shadow-sm p-5 mb-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-3">
                {t('displayTime.sessionsTitle', '検証セッション')}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="py-2 pr-3">{t('displayTime.colSession', '回')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colTime', '時刻（JST）')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colPurpose', '内容')}</th>
                    <th className="py-2 pr-3 text-right">{t('displayTime.colCaptures', '通信')}</th>
                    <th className="py-2 pr-3 text-right">{t('displayTime.colFetched', '取得広告')}</th>
                    <th className="py-2 pr-3 text-right">{t('displayTime.colMeasuredN', '計測')}</th>
                  </tr></thead>
                  <tbody>
                    {data.sessions!.map(se => (
                      <tr key={se.label} className="border-b dark:border-gray-800 text-gray-800 dark:text-gray-200">
                        <td className="py-2 pr-3">{se.label === 'round1' ? t('displayTime.round1', '1回目') : t('displayTime.round2', '2回目')}</td>
                        <td className="py-2 pr-3 tabular-nums">{jst(se.startedAt)} – {jst(se.endedAt).slice(-8)}</td>
                        <td className="py-2 pr-3">{se.purpose.split('／')[0]}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{se.captures}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{se.fetchedAds}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{windows.filter(w => w.session === se.label).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white dark:bg-[#231d17] rounded-xl shadow-sm p-5 mb-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-3">
                {t('displayTime.windowsTitle', '広告ごとの画面表示時間（全件）')}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="py-2 pr-3">{t('displayTime.colSession', '回')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colTab', 'タブ')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colAdvertiser', '広告主')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colOnScreen', '画面表示時間')}</th>
                    <th className="py-2 pr-3 text-right">{t('displayTime.colVideo', '動画 視聴／尺')}</th>
                    <th className="py-2 pr-3">{t('displayTime.colCaptured', '広告取得（JST）')}</th>
                  </tr></thead>
                  <tbody>
                    {windows.map(w => (
                      <tr key={`${w.session}-${w.impressionId}`} className="border-b dark:border-gray-800 text-gray-800 dark:text-gray-200">
                        <td className="py-1.5 pr-3">{w.session === 'round1' ? '1' : '2'}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{tabLabel(w)}</td>
                        <td className="py-1.5 pr-3">
                          {w.joined ? w.advertiserName
                            : <span className="text-amber-600" title={w.impressionId}>
                                {t('displayTime.unjoinedFollowing', '未紐付け（当社パーサ未対応のタブ）')}
                              </span>}
                        </td>
                        <td className="py-1.5 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2.5 rounded bg-teal-500" style={{ width: `${Math.max(2, 160 * w.onScreenMs / maxMs)}px` }} />
                            <span className="tabular-nums">{sec(w.onScreenMs)} {u}</span>
                          </div>
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {w.timeWatchedMs !== null && w.videoLengthMs !== null
                            ? `${sec(w.timeWatchedMs)} / ${sec(w.videoLengthMs)} ${u}` : '—'}
                        </td>
                        <td className="py-1.5 pr-3 tabular-nums whitespace-nowrap">{jst(w.capturedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('displayTime.footnote',
                '画面表示時間＝X が送信する visibility_end − visibility_start（同一端末時計の差分）。検証端末の時計はサーバより +{{o}} 秒進んでいましたが、差分には影響しません。紐付けは impression_id による本番 ad_events との照合です。',
                { o: ((data.sessions![0]?.clockOffsetMs ?? 0) / 1000).toFixed(1) })}
            </p>
          </>
        )}
        </>)}
      </div>
    </div>
  );
}
